import React, { useState, useEffect } from 'react';

function VisitSlotManager() {
    const [slots, setSlots] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingSlot, setEditingSlot] = useState(null);
    const [googleConnected, setGoogleConnected] = useState(false);
    const [activeTab, setActiveTab] = useState('slots');

    const [formData, setFormData] = useState({
        date: '',
        time: '10:00',
        duration: 90,
        max_participants: 1,
        title: 'Kennenlernen + Schnupper-Workshop',
        notes: ''
    });

    useEffect(() => {
        fetchSlots();
        fetchBookings();
        checkGoogleStatus();
    }, []);

    const fetchSlots = () => {
        fetch('/api/visit-slots')
            .then(res => res.json())
            .then(data => setSlots(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Slots:', err));
    };

    const fetchBookings = () => {
        fetch('/api/visit-bookings')
            .then(res => res.json())
            .then(data => setBookings(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Buchungen:', err));
    };

    const checkGoogleStatus = () => {
        fetch('/api/google/status')
            .then(res => res.json())
            .then(data => setGoogleConnected(data.data?.connected || false))
            .catch(err => console.error('Fehler beim Pruefen des Google-Status:', err));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const url = editingSlot
            ? `/api/visit-slots/${editingSlot.id}`
            : '/api/visit-slots';
        const method = editingSlot ? 'PUT' : 'POST';

        fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
        })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchSlots();
                    resetForm();
                }
            })
            .catch(err => console.error('Fehler beim Speichern:', err));
    };

    const handleEdit = (slot) => {
        setEditingSlot(slot);
        setFormData({
            date: slot.date,
            time: slot.time,
            duration: slot.duration,
            max_participants: slot.max_participants,
            title: slot.title,
            notes: slot.notes || ''
        });
        setShowForm(true);
    };

    const handleDelete = (id) => {
        if (!window.confirm('Slot wirklich loeschen? Alle Buchungen werden ebenfalls geloescht.')) {
            return;
        }

        fetch(`/api/visit-slots/${id}`, { method: 'DELETE' })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchSlots();
                    fetchBookings();
                }
            })
            .catch(err => console.error('Fehler beim Loeschen:', err));
    };

    const handleCancelBooking = (id) => {
        if (!window.confirm('Buchung wirklich stornieren?')) {
            return;
        }

        fetch(`/api/visit-bookings/${id}`, { method: 'DELETE' })
            .then(res => res.json())
            .then(data => {
                if (data.message === 'success') {
                    fetchBookings();
                    fetchSlots();
                }
            })
            .catch(err => console.error('Fehler beim Stornieren:', err));
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingSlot(null);
        setFormData({
            date: '',
            time: '10:00',
            duration: 90,
            max_participants: 1,
            title: 'Kennenlernen + Schnupper-Workshop',
            notes: ''
        });
    };

    const connectGoogle = () => {
        window.open('/api/google/auth', '_blank', 'width=500,height=600');
        // Check status after a delay
        setTimeout(() => checkGoogleStatus(), 5000);
        setTimeout(() => checkGoogleStatus(), 10000);
    };

    const disconnectGoogle = () => {
        if (!window.confirm('Google Calendar Verbindung wirklich trennen?')) {
            return;
        }

        fetch('/api/google/disconnect', { method: 'POST' })
            .then(res => res.json())
            .then(() => setGoogleConnected(false))
            .catch(err => console.error('Fehler beim Trennen:', err));
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return (
        <div className="container-fluid">
            <h4>Kennenlernen-Termine verwalten</h4>

            {/* Google Calendar Status */}
            <div className="alert alert-light border mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Google Calendar:</strong>{' '}
                        {googleConnected ? (
                            <span className="text-success">Verbunden</span>
                        ) : (
                            <span className="text-muted">Nicht verbunden</span>
                        )}
                    </div>
                    <div>
                        {googleConnected ? (
                            <button className="btn btn-outline-secondary btn-sm" onClick={disconnectGoogle}>
                                Trennen
                            </button>
                        ) : (
                            <button className="btn btn-outline-primary btn-sm" onClick={connectGoogle}>
                                Mit Google verbinden
                            </button>
                        )}
                    </div>
                </div>
                {!googleConnected && (
                    <small className="text-muted d-block mt-2">
                        Verbinde Google Calendar, damit Buchungen automatisch in deinem Kalender erscheinen.
                    </small>
                )}
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'slots' ? 'active' : ''}`}
                        onClick={() => setActiveTab('slots')}
                    >
                        Termine ({slots.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'bookings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bookings')}
                    >
                        Buchungen ({bookings.length})
                    </button>
                </li>
            </ul>

            {/* Slots Tab */}
            {activeTab === 'slots' && (
                <>
                    <button className="btn btn-primary mb-3" onClick={() => setShowForm(true)}>
                        + Neuer Termin
                    </button>

                    {showForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5>{editingSlot ? 'Termin bearbeiten' : 'Neuer Termin'}</h5>
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Datum</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={formData.date}
                                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Uhrzeit</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={formData.time}
                                                onChange={e => setFormData({ ...formData, time: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Dauer (Min)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.duration}
                                                onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                                                min="30"
                                                step="15"
                                            />
                                        </div>
                                        <div className="col-md-2 mb-3">
                                            <label className="form-label">Max. Teilnehmer</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.max_participants}
                                                onChange={e => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                                                min="1"
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Titel</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.title}
                                            onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Notizen (intern)</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={formData.notes}
                                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                        />
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-success">
                                            {editingSlot ? 'Speichern' : 'Erstellen'}
                                        </button>
                                        <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                            Abbrechen
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {slots.length === 0 ? (
                        <div className="alert alert-info">
                            Noch keine Termine angelegt. Klicke auf "Neuer Termin" um Kennenlernen-Slots zu erstellen.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Datum</th>
                                        <th>Uhrzeit</th>
                                        <th>Dauer</th>
                                        <th>Titel</th>
                                        <th>Buchungen</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {slots.map(slot => (
                                        <tr key={slot.id}>
                                            <td>{formatDate(slot.date)}</td>
                                            <td>{slot.time} Uhr</td>
                                            <td>{slot.duration} Min</td>
                                            <td>{slot.title}</td>
                                            <td>
                                                <span className={`badge ${slot.booked_count >= slot.max_participants ? 'bg-danger' : slot.booked_count > 0 ? 'bg-warning' : 'bg-success'}`}>
                                                    {slot.booked_count} / {slot.max_participants}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                    onClick={() => handleEdit(slot)}
                                                >
                                                    Bearbeiten
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(slot.id)}
                                                >
                                                    Loeschen
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
                <>
                    {bookings.length === 0 ? (
                        <div className="alert alert-info">
                            Noch keine Buchungen vorhanden.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Datum/Zeit</th>
                                        <th>Name</th>
                                        <th>Kontakt</th>
                                        <th>Kind</th>
                                        <th>Nachricht</th>
                                        <th>Status</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookings.map(booking => (
                                        <tr key={booking.id}>
                                            <td>
                                                {formatDate(booking.date)}<br />
                                                <small className="text-muted">{booking.time} Uhr</small>
                                            </td>
                                            <td>{booking.parent_name}</td>
                                            <td>
                                                <a href={`mailto:${booking.email}`}>{booking.email}</a>
                                                {booking.phone && <><br /><small>{booking.phone}</small></>}
                                            </td>
                                            <td>
                                                {booking.child_name || '-'}
                                                {booking.child_age && <><br /><small>{booking.child_age} Jahre</small></>}
                                            </td>
                                            <td>
                                                <small>{booking.message || '-'}</small>
                                            </td>
                                            <td>
                                                <span className={`badge ${booking.status === 'confirmed' ? 'bg-success' : 'bg-secondary'}`}>
                                                    {booking.status === 'confirmed' ? 'Bestaetigt' : booking.status}
                                                </span>
                                                {booking.google_event_id && (
                                                    <span className="badge bg-info ms-1" title="In Google Calendar">GCal</span>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleCancelBooking(booking.id)}
                                                >
                                                    Stornieren
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default VisitSlotManager;
